/**
 * Portfolio pipeline funnel — pure, unit-testable composition over THREE dashboard widget
 * endpoints that already exist and are already fetched independently elsewhere on
 * `/insights/dashboards` (`filings-by-status`, `monetization-outcomes`, `outcome-variance`). No new
 * backend read, no per-Case scan: every count here is a sum already computed server-side.
 *
 * Deliberately narrower than `screens/cases/lifecycle.ts`'s six-stage, single-Case
 * `deriveLifecycle` (Assessment / Qualified spend / Certified / Monetized / Dossier finalized /
 * Filed). The backend has no portfolio-level COUNT aggregate for two of those six signals today:
 * `QualifiedSpendLineApi.aggregateQualifiedSpendByFiscalYear` is $-summed by fiscal year (not
 * case/filing-counted), and no dashboard aggregate port exists for dossiers at all (both are only
 * ever read per-Case, by `OverviewPanel`). Rather than fabricate a count neither module publishes,
 * this tile shows the 4 of 6 stages a real portfolio aggregate backs and discloses the 2 it omits —
 * the same honest-gap posture as the dashboard's own Readiness tile ("no evidence.api port exists
 * yet"). `STEP_LABEL` is reused directly so this tile and the per-Case Lifecycle Progress Tracker
 * name the shared stages identically, never a second drifting label.
 */
import { STEP_LABEL } from '../cases/lifecycle'
import type { OutcomePathBucket, OutcomeVarianceResult, StatusCountBucket } from '../../types/insights'

export type PipelineStageKey = 'assessment' | 'certified' | 'monetized' | 'filedOutcome'

export interface PipelineStage {
  key: PipelineStageKey
  label: string
  count: number
}

/** The `ProgramFilingState` wire value that maps to `deriveLifecycle`'s "Certified" stage (`case_.internal.domain.ProgramFilingState.CERTIFIED.wireValue()`). */
const CERTIFIED_STATUS = 'certified'

/** The two `deriveLifecycle` stages this portfolio tile cannot honestly count yet — surfaced as a disclosure, never fabricated as 0. */
export const OMITTED_STAGE_LABELS: readonly string[] = [STEP_LABEL.qualifiedSpend, STEP_LABEL.dossierFinalized]

/**
 * `filingsByStatus === null` (not yet loaded / not authorized / errored) omits BOTH stages it backs
 * (`assessment`, `certified`) rather than rendering a fabricated 0 — same for the other two sources,
 * each independently gating the one stage it backs.
 */
export function buildPipelineStages(
  filingsByStatus: StatusCountBucket[] | null,
  monetizationOutcomes: OutcomePathBucket[] | null,
  outcomeVariance: OutcomeVarianceResult | null,
): PipelineStage[] {
  const stages: PipelineStage[] = []

  if (Array.isArray(filingsByStatus)) {
    const totalFilings = filingsByStatus.reduce((sum, b) => sum + (b.count ?? 0), 0)
    stages.push({ key: 'assessment', label: STEP_LABEL.assessment, count: totalFilings })
    const certifiedCount = filingsByStatus.find((b) => b.status === CERTIFIED_STATUS)?.count ?? 0
    stages.push({ key: 'certified', label: STEP_LABEL.certified, count: certifiedCount })
  }

  if (Array.isArray(monetizationOutcomes)) {
    const monetizedCount = monetizationOutcomes.reduce((sum, b) => sum + (b.count ?? 0), 0)
    stages.push({ key: 'monetized', label: STEP_LABEL.monetized, count: monetizedCount })
  }

  if (outcomeVariance && Array.isArray(outcomeVariance.buckets)) {
    const resolvedCount = outcomeVariance.buckets.reduce((sum, b) => sum + (b.resolvedCount ?? 0), 0)
    stages.push({ key: 'filedOutcome', label: STEP_LABEL.filedOutcome, count: resolvedCount })
  }

  return stages
}
